import { useRef } from "react";
import { snapToFrame, generateFullTimeline } from "../utils/videoEditor/util";
import { Edit } from "./useEdits";

export function useTimelineInteraction(
  duration: number,
  FPS: number,
  edits: Edit[],
  setEdits: React.Dispatch<React.SetStateAction<Edit[]>>,
  setCurrent: (t: number) => void,
  videoRef: React.RefObject<HTMLVideoElement>
) {
  const timelineRef = useRef<HTMLDivElement>(null);

  const secToPx = (t: number) => {
    if (!timelineRef.current) return 0;
    const track = timelineRef.current.querySelector('.timeline-track');
    if (!track) return 0;
    const w = track.getBoundingClientRect().width;
    return (t / duration) * w;
  };

  const goToFrame = (t: number) => {
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrent(t);
  };

  const startDrag = (idx: number, edge?: string) => (e: MouseEvent) => {
    e.preventDefault();
    if (!timelineRef.current) return;
    const timelineTrack = timelineRef.current.querySelector('.timeline-track');
    if (!timelineTrack) return;
    const trackRect = timelineTrack.getBoundingClientRect();
    const pxToSec = duration / trackRect.width;
    let dragOffset = 0;

    if (!edge) {
      const x = e.clientX - trackRect.left;
      dragOffset = x * pxToSec - edits[idx].start;
    }

    const onMove = (moveEvent: MouseEvent) => {
      const x = moveEvent.clientX - trackRect.left;
      const t = Math.max(0, Math.min(duration, x * pxToSec));
      const snappedT = snapToFrame(t, FPS);

      setEdits((prev) => {
        const seg = prev[idx];
        if (!seg) return prev;
        if (edge === "start") {
          const prevEnd = idx > 0 ? prev[idx - 1].end : 0;
          const nextStart = seg.end - 1 / FPS;
          const nextStartClamped = Math.max(prevEnd, Math.min(nextStart, snappedT));
          return generateFullTimeline(
            prev.map((s, i) =>
              i === idx ? { ...s, start: parseFloat(nextStartClamped.toFixed(3)) } : s
            ),
            duration
          ).filter((s: any) => s.speed !== 1);
        } else if (edge === "end") {
          const nextSegStart = idx < prev.length - 1 ? prev[idx + 1].start : duration;
          const minEnd = seg.start + 1 / FPS;
          const maxEnd = nextSegStart;
          const nextEndClamped = Math.max(minEnd, Math.min(maxEnd, snappedT));
          return generateFullTimeline(
            prev.map((s, i) =>
              i === idx ? { ...s, end: parseFloat(nextEndClamped.toFixed(3)) } : s
            ),
            duration
          ).filter((s: any) => s.speed !== 1);
        } else {
          // drag entier segment
          const segWidth = seg.end - seg.start;
          const prevEnd = idx > 0 ? prev[idx - 1].end : 0;
          const nextStart = idx < prev.length - 1 ? prev[idx + 1].start : duration;
          let newStart = Math.max(prevEnd, Math.min(nextStart - segWidth, snappedT - dragOffset));
          newStart = Math.max(0, Math.min(duration - segWidth, newStart));
          return generateFullTimeline(
            prev.map((s, i) =>
              i === idx
                ? {
                    ...s,
                    start: parseFloat(newStart.toFixed(3)),
                    end: parseFloat((newStart + segWidth).toFixed(3)),
                  }
                : s
            ),
            duration
          ).filter((s: any) => s.speed !== 1);
        }
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return {
    timelineRef,
    secToPx,
    goToFrame,
    startDrag,
  };
}